/**
 * OFFER PROGRESS (Custom — Maison Afford Offer-Stufen)
 *
 * Two custom elements:
 *  - <offer-progress>        rendered by snippets/offer-progress.liquid; animates the fill bar, celebrates
 *                            newly reached tiers (confetti + node pop) and broadcasts its state.
 *  - <offer-gift-controller> rendered once via snippets/offer-engine.liquid; keeps the free gift line in
 *                            sync with the gift tier (auto add / auto remove via the AJAX Cart API, using the
 *                            theme's bundled-section rendering so the drawer refreshes itself).
 *
 * The source of truth is always the server-rendered <offer-progress> element: Impact re-renders the cart
 * drawer section on every cart mutation, so a fresh element (with fresh data attributes) connects after
 * each change — no client-side cart math needed.
 */
(function () {
  'use strict';

  var SS_FILL = 'afford:offer:fill';
  var SS_FILL_FROM = 'afford:offer:fill-from';
  var SS_REACHED = 'afford:offer:reached';
  var SS_COUNT = 'afford:offer:count';

  function clampFill(value) {
    return Math.min(1, Math.max(0, parseFloat(value || '0') || 0));
  }
  var SS_DISMISSED = 'afford:offer:gift-dismissed';
  var SS_HAD_GIFT = 'afford:offer:gift-was-in-cart';

  function ssGet(key, fallback) {
    try { return window.sessionStorage.getItem(key) || fallback; } catch (e) { return fallback; }
  }

  function ssSet(key, value) {
    try { window.sessionStorage.setItem(key, value); } catch (e) { /* private mode */ }
  }

  function ssRemove(key) {
    try { window.sessionStorage.removeItem(key); } catch (e) { /* private mode */ }
  }

  var reducedMotion = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  /* ------------------------------------------------------------------ */
  /* <offer-progress>                                                    */
  /* ------------------------------------------------------------------ */

  var OfferProgress = (function () {
    function ctor() {
      return Reflect.construct(HTMLElement, [], ctor);
    }

    ctor.prototype = Object.create(HTMLElement.prototype, { constructor: { value: ctor } });

    ctor.prototype.connectedCallback = function () {
      var context = this.dataset.context || '';

      // Only one teaser on the product page (buy-buttons can render more than once).
      if (context === 'product') {
        var teasers = document.querySelectorAll('offer-progress[data-context="product"]');
        if (teasers.length > 1 && teasers[0] !== this) {
          this.remove();
          return;
        }
      }

      this._prepareFill();
      this._observeVisibility();

      var self = this;
      // Let the drawer finish its own DOM swap before controllers react.
      window.setTimeout(function () {
        if (self.isConnected) {
          document.dispatchEvent(new CustomEvent('offer-progress:state', { detail: { element: self } }));
        }
      }, 50);
    };

    ctor.prototype.disconnectedCallback = function () {
      if (this._io) {
        this._io.disconnect();
        this._io = null;
      }
    };

    // Work out where this render's fill animation starts. All instances of the same "round"
    // (one cart mutation re-renders several bars) animate from the same origin, stored in
    // sessionStorage, so the movement is visible everywhere — not just in the first bar.
    ctor.prototype._prepareFill = function () {
      this._fillEl = this.querySelector('.offer-progress__fill');
      var target = clampFill(this.dataset.fill);
      var from;

      var storedRaw = ssGet(SS_FILL, null);
      if (storedRaw === null) {
        from = 0;
        ssSet(SS_FILL_FROM, '0');
      } else {
        var stored = clampFill(storedRaw);
        if (stored === target) {
          from = clampFill(ssGet(SS_FILL_FROM, '0'));
        } else {
          from = stored;
          ssSet(SS_FILL_FROM, String(from));
        }
      }
      ssSet(SS_FILL, String(target));

      this._fillFrom = from;
      this._fillTarget = target;
      // Replay the fill whenever the bar is revealed again inside the cart drawer;
      // static placements (product page, cart page) animate on first reveal only.
      this._replayOnReveal = !!this.closest('cart-drawer');

      if (this._fillEl) {
        this._fillEl.style.setProperty('--offer-fill', String(reducedMotion ? target : from));
      }
    };

    // Animations only run once the bar is actually visible (drawer opened, scrolled into view).
    ctor.prototype._observeVisibility = function () {
      var self = this;

      if (reducedMotion || !('IntersectionObserver' in window)) {
        if (this._fillEl) this._fillEl.style.setProperty('--offer-fill', String(this._fillTarget));
        this._runCelebrations();
        return;
      }

      this._io = new IntersectionObserver(function (entries) {
        entries.forEach(function (entry) {
          if (entry.isIntersecting) {
            self._playFill();
            self._runCelebrations();
          } else if (self._replayOnReveal) {
            self._resetFill();
          }
        });
      }, { threshold: 0.35 });

      this._io.observe(this);
    };

    ctor.prototype._playFill = function () {
      var self = this;
      if (!this._fillEl) return;
      window.requestAnimationFrame(function () {
        window.requestAnimationFrame(function () {
          if (self._fillEl) self._fillEl.style.setProperty('--offer-fill', String(self._fillTarget));
        });
      });
    };

    ctor.prototype._resetFill = function () {
      if (this._fillEl) this._fillEl.style.setProperty('--offer-fill', String(this._fillFrom));
    };

    ctor.prototype._runCelebrations = function () {
      if (this._celebrated) return;
      this._celebrated = true;
      var rewardCelebrated = this._celebrateNewTiers();
      this._celebrateStep(rewardCelebrated);
    };

    ctor.prototype._celebrateNewTiers = function () {
      if (this.dataset.context === 'announcement') return false;

      var reachedNow = (this.dataset.reachedKeys || '').split(',').filter(Boolean);
      var reachedBefore = [];
      try { reachedBefore = JSON.parse(ssGet(SS_REACHED, '[]')); } catch (e) { reachedBefore = []; }

      var newly = reachedNow.filter(function (key) { return reachedBefore.indexOf(key) === -1; });
      var lost = reachedBefore.filter(function (key) { return reachedNow.indexOf(key) === -1; });

      // Persist as soon as one instance saw the change so parallel instances don't double-celebrate.
      if (newly.length || lost.length) {
        ssSet(SS_REACHED, JSON.stringify(reachedNow));
      }

      if (!newly.length || reducedMotion) return false;

      var self = this;
      this.classList.add('offer-progress--celebrate');

      newly.forEach(function (key) {
        var node = self.querySelector('[data-tier="' + key + '"]');
        if (node) node.classList.add('offer-progress__node--pop');
      });

      this._burstConfetti();

      if (window.navigator.vibrate) {
        try { window.navigator.vibrate([35, 40, 35]); } catch (e) { /* not allowed */ }
      }

      window.setTimeout(function () {
        self.classList.remove('offer-progress--celebrate');
      }, 2200);

      return true;
    };

    // Small treat on EVERY added perfume: pop the step dot, mini confetti at the fill edge.
    // The big reward celebration (when a gift tier unlocks) takes precedence.
    ctor.prototype._celebrateStep = function (rewardCelebrated) {
      if (this.dataset.context === 'announcement') return;

      var previousCount = parseInt(ssGet(SS_COUNT, '0'), 10);
      var currentCount = parseInt(this.dataset.perfumeCount || '0', 10);

      if (currentCount === previousCount) return;
      ssSet(SS_COUNT, String(currentCount));

      if (currentCount < previousCount || rewardCelebrated || reducedMotion) return;

      var step = this.querySelector('.offer-progress__step[data-step="' + currentCount + '"]');
      if (step) step.classList.add('offer-progress__step--pop');

      var fillPercent = Math.min(1, Math.max(0, parseFloat(this.dataset.fill || '0'))) * 100;
      this._burstConfetti(12, fillPercent);

      var self = this;
      this.classList.add('offer-progress--step-celebrate');
      window.setTimeout(function () {
        self.classList.remove('offer-progress--step-celebrate');
      }, 900);

      if (window.navigator.vibrate) {
        try { window.navigator.vibrate(25); } catch (e) { /* not allowed */ }
      }
    };

    ctor.prototype._burstConfetti = function (count, centerPercent) {
      var track = this.querySelector('.offer-progress__track');
      if (!track) return;

      var colors = ['#ff8682', '#f0c417', '#1a1a1a', '#ffffff'];
      var total = count || 26;

      for (var i = 0; i < total; i++) {
        var piece = document.createElement('span');
        piece.className = 'offer-confetti';
        var angle = Math.random() * Math.PI - Math.PI; // upwards half circle
        var distance = 40 + Math.random() * 70;
        piece.style.setProperty('--confetti-x', Math.cos(angle) * distance + 'px');
        piece.style.setProperty('--confetti-y', Math.sin(angle) * distance - 20 + 'px');
        piece.style.setProperty('--confetti-rotation', (Math.random() * 540 - 270) + 'deg');
        piece.style.setProperty('--confetti-delay', (Math.random() * 120) + 'ms');
        if (typeof centerPercent === 'number') {
          piece.style.left = Math.min(97, Math.max(3, centerPercent - 12 + Math.random() * 24)) + '%';
        } else {
          piece.style.left = 55 + Math.random() * 40 + '%';
        }
        piece.style.backgroundColor = colors[i % colors.length];
        if (i % 3 === 0) piece.style.borderRadius = '50%';
        track.appendChild(piece);
      }

      window.setTimeout(function () {
        track.querySelectorAll('.offer-confetti').forEach(function (piece) { piece.remove(); });
      }, 1800);
    };

    // Adopt the fresh server-rendered state of another instance (used to live-update the
    // static product-page widget from the re-rendered cart drawer).
    ctor.prototype.syncFrom = function (source) {
      var self = this;
      ['fill', 'reachedKeys', 'perfumeCount', 'giftReached', 'giftInCart', 'giftProductInCart', 'giftLineKey', 'giftVariantId', 'giftAvailable'].forEach(function (key) {
        if (source.dataset[key] !== undefined) self.dataset[key] = source.dataset[key];
      });
      this.innerHTML = source.innerHTML;
      this.querySelectorAll('.offer-confetti').forEach(function (piece) { piece.remove(); });

      // Animate from this widget's previous state to the fresh value (the copied markup may
      // carry a mid-animation fill value, so both ends are set explicitly).
      var previousTarget = typeof this._fillTarget === 'number' ? this._fillTarget : clampFill(this.dataset.fill);
      this._fillEl = this.querySelector('.offer-progress__fill');
      this._fillTarget = clampFill(this.dataset.fill);
      this._fillFrom = previousTarget;

      if (this._fillEl) {
        this._fillEl.style.setProperty('--offer-fill', String(reducedMotion ? this._fillTarget : this._fillFrom));
      }
      if (!reducedMotion) this._playFill();
    };

    return ctor;
  })();

  if (!window.customElements.get('offer-progress')) {
    window.customElements.define('offer-progress', OfferProgress);
  }

  // Live-update the static product-page widget whenever a freshly server-rendered instance
  // (cart drawer / added notification) connects after a cart mutation.
  document.addEventListener('offer-progress:state', function (event) {
    var source = event.detail && event.detail.element;
    if (!source || !source.isConnected) return;

    var context = source.dataset.context;
    if (context !== 'drawer' && context !== 'added') return;

    document.querySelectorAll('offer-progress[data-context="product"]').forEach(function (widget) {
      if (widget !== source && widget.isConnected && typeof widget.syncFrom === 'function') {
        widget.syncFrom(source);
      }
    });
  });

  /* ------------------------------------------------------------------ */
  /* <offer-gift-controller>                                             */
  /* ------------------------------------------------------------------ */

  var OfferGiftController = (function () {
    function ctor() {
      return Reflect.construct(HTMLElement, [], ctor);
    }

    ctor.prototype = Object.create(HTMLElement.prototype, { constructor: { value: ctor } });

    ctor.prototype.connectedCallback = function () {
      this._busy = false;
      this._onStateListener = this._onState.bind(this);
      document.addEventListener('offer-progress:state', this._onStateListener);
    };

    ctor.prototype.disconnectedCallback = function () {
      document.removeEventListener('offer-progress:state', this._onStateListener);
    };

    ctor.prototype._onState = function (event) {
      var el = event.detail && event.detail.element;
      if (!el || !el.isConnected || this._busy) return;
      if (el.dataset.context === 'announcement') return;

      var reached = el.dataset.giftReached === 'true';
      var inCart = el.dataset.giftInCart === 'true';
      // The customer may have bought the gift product on their own (e.g. via the 3,90 € page) —
      // never add a second one on top, and never touch that manually added line.
      var productInCart = el.dataset.giftProductInCart === 'true';
      var available = el.dataset.giftAvailable === 'true';
      var variantId = parseInt(el.dataset.giftVariantId || '0', 10);
      var lineKey = el.dataset.giftLineKey || '';

      // Tier lost → forget the manual dismissal so the gift re-adds on the next unlock.
      if (!reached) ssRemove(SS_DISMISSED);

      // Tier reached, gift no longer in cart although it was → the customer removed it on purpose.
      if (reached && !inCart && ssGet(SS_HAD_GIFT, '0') === '1') {
        ssSet(SS_DISMISSED, '1');
      }

      ssSet(SS_HAD_GIFT, inCart ? '1' : '0');

      if (this.dataset.autoAdd !== 'true') return;

      var self = this;

      if (reached && !inCart && !productInCart && available && variantId && ssGet(SS_DISMISSED, '') !== '1') {
        // Give the tier celebration a moment before the drawer re-renders with the gift line.
        this._busy = true;
        window.setTimeout(function () {
          self._mutateCart('add', { id: variantId, quantity: 1, properties: { _offer_gift: '1' } });
        }, reducedMotion ? 0 : 950);
      } else if (!reached && inCart && lineKey) {
        this._busy = true;
        this._mutateCart('change', { id: lineKey, quantity: 0 });
      }
    };

    ctor.prototype._mutateCart = function (kind, payload) {
      var self = this;
      var root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';

      var sectionsToBundle = [];
      document.documentElement.dispatchEvent(new CustomEvent('cart:prepare-bundled-sections', {
        bubbles: true,
        detail: { sections: sectionsToBundle }
      }));

      var url = root + (kind === 'add' ? 'cart/add.js' : 'cart/change.js');
      var body = kind === 'add'
        ? { items: [payload], sections: sectionsToBundle.join(',') }
        : { id: payload.id, quantity: payload.quantity, sections: sectionsToBundle.join(',') };

      window.fetch(url, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
        body: JSON.stringify(body)
      }).then(function (response) {
        if (!response.ok) throw new Error('offer-gift: cart mutation failed');
        return response.json();
      }).then(function (responseJson) {
        var themeSettings = (window.themeVariables && window.themeVariables.settings) || {};

        if (themeSettings.pageType === 'cart') {
          window.location.reload();
          return null;
        }

        return window.fetch(root + 'cart.js')
          .then(function (response) { return response.json(); })
          .then(function (cartContent) {
            cartContent.sections = responseJson.sections;
            document.documentElement.dispatchEvent(new CustomEvent('cart:change', {
              bubbles: true,
              detail: { baseEvent: 'offer-gift:change', cart: cartContent }
            }));
          });
      }).catch(function () {
        /* Swallow — worst case the gift stays manual; discount logic is enforced by Shopify anyway. */
      }).then(function () {
        self._busy = false;
      });
    };

    return ctor;
  })();

  if (!window.customElements.get('offer-gift-controller')) {
    window.customElements.define('offer-gift-controller', OfferGiftController);
  }
})();
